import React from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowRightIcon,
	CheckCircleIcon,
} from '@heroicons/react/24/outline';
import seo from './SEO/data';
import GABE_SELFIE from '../assets/images/2026/gabe-selfie.png';

// "Why Homeowners Choose Me Instead of the Cheapest Solar Quote" — per the
// consultant's feedback this is the single highest-value page: it's the entire
// sales process, told the same way Gabe tells it at the kitchen table.

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

// What homeowners often don't see when they chase the lowest price.
const mattersList = [
	{
		title: 'Installation quality',
		body: 'How the array is mounted, flashed, and wired determines whether your roof stays watertight for decades.',
	},
	{
		title: 'Project manager experience',
		body: 'The person running your project decides how smoothly permitting, inspection, and PTO actually go.',
	},
	{
		title: 'Equipment quality',
		body: 'Panels, inverters, and batteries vary widely in efficiency, degradation, and warranty.',
	},
	{
		title: 'Warranty quality',
		body: 'A 25-year warranty is only as good as the company standing behind it.',
	},
	{
		title: "The company's reputation",
		body: 'Reviews, track record, and how they treat customers after the sale matter most.',
	},
	{
		title: 'Service after installation',
		body: 'Solar is a long-term relationship. You want someone who answers the phone in year three.',
	},
];

export default function WhyChooseGabe() {
	return (
		<div className='bg-surface text-ink-900 antialiased'>
			{/* Hero */}
			<section className='relative isolate overflow-hidden bg-ink-900'>
				<div
					className='absolute inset-0 -z-10 opacity-25'
					style={{
						backgroundImage:
							'radial-gradient(circle at 20% 20%, #3CB371 0%, transparent 45%), radial-gradient(circle at 85% 90%, #2A7E4F 0%, transparent 55%)',
					}}
				/>
				<div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
					<div className='max-w-3xl'>
						<Eyebrow dark>Why I&apos;m different</Eyebrow>
						<h1 className='font-display mt-6 text-4xl font-medium tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.08]'>
							Why homeowners choose me instead of the{' '}
							<span className='italic text-primary-400'>cheapest</span> solar
							quote.
						</h1>
						<p className='mt-8 max-w-2xl text-lg leading-8 text-white/75'>
							My name is Gabriel Shabtai, and before I ever sat at a kitchen
							table discussing solar, I spent years installing systems on roofs,
							troubleshooting electrical, running crews, and managing projects.
						</p>
					</div>
				</div>
			</section>

			{/* My goal */}
			<section className='bg-surface py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center'>
						<div className='lg:col-span-7'>
							<Eyebrow>My goal</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
								Educate first. Never pressure.
							</h2>
							<div className='mt-6 space-y-5 text-base leading-7 text-ink-500'>
								<p>
									Today, I help Maryland homeowners make informed decisions about
									solar. My goal isn&apos;t to pressure anyone into signing a
									contract — it&apos;s to educate homeowners so they fully
									understand their options.
								</p>
								<p>
									The reality is that many solar companies can offer lower
									prices. What homeowners often don&apos;t see is what happens
									<em> after</em> the contract is signed.
								</p>
							</div>
						</div>
						<div className='lg:col-span-5'>
							<div className='relative mx-auto max-w-sm'>
								<div className='absolute -inset-6 rounded-3xl bg-primary-100/70 blur-2xl' />
								<img
									src={GABE_SELFIE}
									alt='Gabriel Shabtai on his property'
									className='relative aspect-[4/5] w-full rounded-3xl object-cover object-top shadow-xl shadow-ink-900/10 ring-1 ring-ink-100'
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* What matters */}
			<section className='bg-white border-y border-ink-100 py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='mx-auto max-w-2xl text-center'>
						<div className='flex justify-center'>
							<Eyebrow>What actually matters</Eyebrow>
						</div>
						<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
							A solar system should last 25–30 years.
						</h2>
						<p className='mt-6 text-base leading-7 text-ink-500'>
							Choosing the right team can save thousands of dollars and years of
							frustration.
						</p>
					</div>
					<div className='mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
						{mattersList.map((m) => (
							<div
								key={m.title}
								className='sg-card p-7'
							>
								<CheckCircleIcon className='h-7 w-7 text-primary-500' />
								<h3 className='font-display mt-5 text-xl font-medium text-ink-900'>
									{m.title}
								</h3>
								<p className='mt-2 text-sm leading-6 text-ink-500'>{m.body}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Second opinion offer */}
			<section className='bg-surface py-20 sm:py-28'>
				<div className='mx-auto max-w-3xl px-6 text-center lg:px-8'>
					<Eyebrow>Comparing quotes?</Eyebrow>
					<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
						When you work with me, you work directly with someone who
						understands solar from the roof through PTO.
					</h2>
					<p className='mt-6 text-base leading-7 text-ink-500'>
						If you&apos;re comparing quotes, I&apos;d be happy to provide a
						second opinion — even if you don&apos;t choose me.
					</p>
					<div className='mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4'>
						<Link
							to='/contact'
							className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 transition-all'
						>
							Get a second opinion
							<ArrowRightIcon className='h-5 w-5' />
						</Link>
						<a
							href={`tel:${seo.company.phone}`}
							className='text-sm font-semibold text-ink-700 hover:text-primary-600'
						>
							or call {seo.company.phone}
						</a>
					</div>
				</div>
			</section>
		</div>
	);
}
